import { pickValue } from './objects';

/**
 * Change first letter for a string
 *
 * @param {string} str String to change
 * @param {boolean} capitalize If capitalize change first letter to uppercase
 * @returns {string} Modified string
 */
function firstLetter(str, capitalize = true) {
  let output = str.charAt(0)[capitalize ? 'toUpperCase' : 'toLowerCase']();
  output += str.slice(1);

  return output;
}

/**
 * Parse string values to JavaScript data types
 *
 * @param {string} str
 * @returns {any}
 */
function parseValue(str) {
  let val = str.trim();

  val =
    {
      false: false,
      true: true,
    }[val] ?? val;

  if (String(parseFloat(val)) === val) {
    val = parseFloat(val);
  }

  return val;
}

/**
 * Pupulate string with values in object
 *
 * @param {string} str String to populate
 * @param {object} values Data to populate string
 * @param {string} prefix Prefix for replacement values
 * @returns {string} Populate string
 */
function populate(str, values = {}, prefix = ':') {
  let output = str;

  for (const [k, v] of Object.entries(values)) {
    const key = `${prefix}${k}`;

    output =
      v !== null && typeof v === 'object'
        ? populate(output, v, `${key}.`)
        : replaceAll(output, key, v);
  }

  return output;
}

/**
 *
 * @param {*} str
 * @param {*} search
 * @param {*} replacement
 * @returns
 */
function replaceAll(str, search, replacement) {
  return str.split(search).join(replacement);
}

/**
 * Slugifies a string
 *
 * @param {string} str String to slugify
 * @param {object} options Obtions for string format
 * @returns {string}
 */
function slugify(str, options) {
  const { replacement = '_', upperCase = false } = options || {};

  let output = str.replace(/^\s+|\s+$/g, '').toLowerCase();

  const from = 'àáäâèéëêìíïîòóöôùúüûñç·/_,:;';
  const to = 'aaaaeeeeiiiioooouuuunc------';
  for (let i = 0, l = from.length; i < l; i++) {
    output = output.replace(new RegExp(from.charAt(i), 'g'), to.charAt(i));
  }

  output = output
    .replace(/[^a-z0-9 -]/g, '')
    .replace(/\s+/g, replacement)
    .replace(/-+/g, replacement);

  if (upperCase) {
    output = output.toUpperCase();
  }

  return output;
}

/**
 * Generate string from template
 *
 * @param {string} str Input template string
 * @param {object} values Data to populate template
 * @param {*} options
 * @returns {string}
 */
function renderString(str, values = {}, options = {}) {
  if (typeof str !== 'string') return '';

  try {
    const pattern = /{([^{}]+)}/g;
    const separator = options?.separator ?? '.';

    // Build a fieldname → options lookup for select fields so IDs are resolved to labels.
    // Activated when callers pass { template: peopleType.template } in options.
    // Handles both array ([{id, name}]) and object ({id: name}) formats for field.options.
    const selectResolvers = {};
    if (Array.isArray(options?.template)) {
      for (const field of options.template) {
        if (field.displaytype === 'select' && field.options && field.fieldname) {
          if (Array.isArray(field.options) && field.options.length > 0) {
            selectResolvers[field.fieldname] = field.options;
          } else if (typeof field.options === 'object' && !Array.isArray(field.options)) {
            selectResolvers[field.fieldname] = Object.entries(field.options).map(([id, name]) => ({
              id,
              name,
            }));
          }
        }
      }
    }

    const replacer = (_, key) => {
      const trimmedKey = key.trim();
      try {
        const rawValue = pickValue(values, trimmedKey, separator) ?? '';
        if (rawValue !== '' && rawValue !== null && rawValue !== undefined) {
          const fieldname = trimmedKey.split(separator).pop();
          const fieldOptions = selectResolvers[fieldname];
          if (fieldOptions) {
            const match = fieldOptions.find((opt) => String(opt.id) === String(rawValue));
            if (match) return match.name;
          }
        }
        return rawValue;
      } catch {
        return '';
      }
    };

    return str.replace(pattern, replacer);
  } catch (error) {
    console.error('Error en renderString:', error);
    return '';
  }
}

/**
 * Truncate text to specified length and add ellipsis if needed
 *
 * @param {string} text Text to truncate
 * @param {number} maxLength Maximum length before truncation
 * @returns {string} Truncated text
 */
function truncateText(text, maxLength = 50) {
  if (!text || typeof text !== 'string') return text || '';
  return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
}
/**
 * Apply a text transformation to a string value
 *
 * @param {string} value String to transform
 * @param {'uppercase'|'lowercase'|'capitalize'|'titlecase'} transform Transformation to apply
 * @returns {string} Transformed string
 */
function applyTextTransform(value, transform) {
  if (!transform || typeof value !== 'string') return value;
  switch (transform) {
    case 'uppercase':
      return value.toUpperCase();
    case 'lowercase':
      return value.toLowerCase();
    case 'capitalize':
      return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
    case 'titlecase':
      return value.replace(/\S+/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
    default:
      return value;
  }
};

const renderSubtitle = (template, data, options = {}) => {
  const originalResult = renderString(template, data, options);

  return originalResult.replace(/[^\s]{51,}/g, (match) => {
    return `${match.substring(0, 50)}...`;
  });
};

const fixEncoding = (str) => {
  if (!str || typeof str !== 'string') return str;
  try {
    return decodeURIComponent(escape(str));
  } catch (e) {
    return str;
  }
};

/**
 * Generate full name
 *
 * @param {string} str Input template string
 * @param {object} values Data to populate template
 * @param {*} options
 * @returns {string}
 */
function resolveShareholderName(fullname = '', people = {}) {
  try {
    if (!fullname || !fullname.trim()) return '';

    const trimmed = fullname.trim();

    const placeholders = trimmed.match(/\{[^}]+\}/g);

    if (!placeholders) return '';
    
    if (placeholders.length === 1 && trimmed === placeholders[0]) {
      return renderString(trimmed, people) || '';
    }

    const values = placeholders.map((p) => renderString(p, people));
    const [type, individual, corporate] = values;

    if (type === 'Corporate') return corporate || '';
    if (type === 'Individual') return individual || '';

    return '';
  } catch (error) {
    console.error('Error resolving shareholder name:', error);
    return '';
  }
}

export {
  applyTextTransform,
  firstLetter,
  parseValue,
  populate,
  replaceAll,
  renderString,
  slugify,
  truncateText,
  renderSubtitle,
  fixEncoding,
  resolveShareholderName,
};
